import React from 'react';
import { useSelector } from 'react-redux';
import { Flex } from '@adobe/react-spectrum';

import { BaseDeckPane } from './BaseDeckPane';
import { getPrettyLabel } from '../../../data/CategoryPrettyDisplayUtils';
import { deriveProfileMarkers, deriveProfileScoreDrivers } from '../../../data/deckProfileMarkers';
import { PowerLevelFingerprint } from './PowerLevelFingerprint';
import './PowerLevelPane.css';

const POWER_TIERS = [
    { min: 9, label: 'Competitive', tone: 'alert' },
    { min: 7, label: 'Optimized', tone: 'alert' },
    { min: 5, label: 'Focused Casual', tone: 'watch' },
    { min: 3, label: 'Casual', tone: 'ok' },
    { min: 0, label: 'Exhibition', tone: 'ok' },
];

export function ScoreDriverRow({ entry, direction }) {
    const pct = Math.max(0, Math.min(100, Math.round(entry.ratio * 100)));
    return (
        <div className={`powerDriverRow powerDriverRow--${direction}`}>
            <span className="powerDriverLabel">
                {entry.label}
                {entry.capped && (
                    <span className="powerDriverCapBadge" title="Penalty applied at its maximum cap.">
                        capped
                    </span>
                )}
            </span>
            <div className="powerDriverBarTrack">
                <div className="powerDriverBarFill" style={{ width: `${pct}%` }} />
            </div>
            <span className="powerDriverMagnitude">{entry.magnitudeLabel}</span>
        </div>
    );
}

const PILLARS = [
    { key: 'consistency', label: 'Consistency' },
    { key: 'efficiency', label: 'Efficiency' },
    { key: 'interaction', label: 'Interaction' },
    { key: 'winConditions', label: 'Win Conditions' },
];

const PILLAR_CHART_MAX_RATIO = 1.5;

function powerTier(rating) {
    const r = Number(rating);
    if (Number.isNaN(r)) return POWER_TIERS[POWER_TIERS.length - 1];
    for (const tier of POWER_TIERS) {
        if (r >= tier.min) return tier;
    }
    return POWER_TIERS[POWER_TIERS.length - 1];
}

function pillarIndicator(ratio) {
    if (ratio >= 1.25) return 'above';
    if (ratio <= 0.75) return 'below';
    return 'match';
}

function PillarBar({ pillar, score, baseline }) {
    const ratio = baseline > 0 ? score / baseline : 0;
    const pct = Math.round(ratio * 100);
    const indicator = pillarIndicator(ratio);
    const fillPct = Math.min(ratio / PILLAR_CHART_MAX_RATIO, 1) * 100;
    const baselineTickPct = (1 / PILLAR_CHART_MAX_RATIO) * 100;

    return (
        <div className={`powerPillarRow powerPillarRow--${indicator}`}>
            <span className="powerPillarRowLabel">{pillar.label}</span>
            <div className="powerPillarRowTrack">
                <div className="powerPillarRowFill" style={{ width: `${fillPct}%` }} />
                <div className="powerPillarRowTick" style={{ left: `${baselineTickPct}%` }} />
            </div>
            <span className="powerPillarRowScore">
                <strong>{Math.round(score)}</strong> <span className="powerPillarRowSlash">/</span>{' '}
                {Math.round(baseline)}
            </span>
            <span className={`powerPillarRowDelta powerPillarRowDelta--${indicator}`}>
                {indicator === 'above' ? '▲' : indicator === 'below' ? '▼' : '═'} {pct}%
            </span>
        </div>
    );
}

export function PowerLevelPane() {
    const deck = useSelector((state) => state.deck.deck);
    const cards = useSelector((state) => state.deck.cards);

    const isFringe =
        deck?.details?.powerLevel?.ratings?.fringeCEDH === true && parseFloat(deck?.powerLevelRating) >= 8.7;
    const useCEDH = deck?.details?.powerLevel?.ratings?.inferredType === 'spike' || isFringe;

    const [useCEDHPowerLevels, setUseCEDHPowerLevels] = React.useState(useCEDH);
    const type = useCEDHPowerLevels ? 'spike' : 'casual';

    const [fingerprintOpen, setFingerprintOpen] = React.useState(() => {
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return true;
        return window.matchMedia('(min-width: 768px)').matches;
    });

    const scoring = deck?.details?.powerLevel?.scoring || {};
    const baseValues = deck?.details?.powerLevel?.baseValues?.[type] || {};
    const ratings = deck?.details?.powerLevel?.ratings || {};

    const powerLevel = Number(deck?.powerLevelRating) || 0;
    // Truncate (not round) to 1 decimal so 5.97 → 5.9 rather than 6.0. Rounding up
    // past the integer bracket on the assessment gauge looked inconsistent.
    const powerLevelDisplay = (Math.floor(powerLevel * 10) / 10).toFixed(1);
    const tier = powerTier(powerLevel);
    const pct = Math.max(0, Math.min(100, (powerLevel / 10) * 100));

    const profile = deck?.details?.powerLevel?.profile;
    const profileMarkers = deriveProfileMarkers(profile);
    const scoreDrivers = deriveProfileScoreDrivers(profile);
    const hasFingerprintContent =
        !!profile ||
        profileMarkers.length > 0 ||
        (scoreDrivers && scoreDrivers.hasAnyContent);

    const getBlurbs = () => ({
        tutors: {
            headerLabel: 'Tutors',
            tooMany: { casual: '', spike: '' },
            tooFew: {
                casual: '',
                spike: `Tutors greatly increase consistency. ${
                    deck?.details?.powerLevel?.ratings?.spike?.stax >= 7
                        ? 'You appear to have a fair amount of stax effects... do any of these punish other people for tutoring?'
                        : 'Consider adding more if that is an option.'
                }`,
            },
        },
        answers: {
            headerLabel: 'Interaction',
            tooMany: {
                casual: 'Casual players typically run very little interaction. If you are running much more than everyone else, you will most likely be archenemy.',
                spike: '',
            },
            tooFew: {
                casual: '',
                spike:
                    deck?.details?.powerLevel?.ratings?.spike?.stax >= 10
                        ? 'You have a lot of stax effects, which is great. However, your instant-speed answers appear to be low. Consider increasing this if you have issues closing out games.'
                        : 'Competitive players run a high amount of interaction in order to protect their win conditions. If you are having issues closing out games, consider increasing this.',
            },
        },
        combos: {
            headerLabel: 'Combos',
            tooMany: {
                casual: '',
                spike:
                    deck?.details?.powerLevel?.ratings?.spike?.answers <= 7
                        ? 'Are you running enough interaction to protect your combos?'
                        : '',
            },
            tooFew: { casual: '', spike: '' },
        },
        stax: {
            headerLabel: 'Stax',
            tooMany: {
                casual: 'People looking for casual games tend to strongly dislike stax effects as it prevents them from having fun. Additionally, casual players typically run far less interaction than competitive players, so you are essentially playing these effects into a vacuum.',
                spike: '',
            },
            tooFew: { casual: '', spike: '' },
        },
        fastmana: {
            headerLabel: 'Fast mana',
            tooMany: {
                casual: 'Having a lot of fast mana pieces gives you a board state that is often way ahead of everyone else in a casual pod. Consider lowering this if you are primarily playing this in non cEDH / high power settings.',
                spike: '',
            },
            tooFew: {
                casual: '',
                spike: 'Fast mana pieces like Mana Crypt are pretty necessary for higher power games, as they increase your ability to quickly assemble a board state. Since other people will be running a lot of these, you may be at a disadvantage.',
            },
        },
    });

    const getDiscussionPoints = () => {
        const list = [];
        const casualBase = deck?.details?.powerLevel?.baseValues?.casual || {};
        Object.keys(casualBase)?.forEach((category) => {
            const actual = deck?.details?.powerLevel?.scoring?.[category]?.score;
            const baseline = casualBase[category];
            const blurbMap = getBlurbs()?.[category];
            if (!blurbMap || !actual || !baseline) return;

            let direction = null;
            if (actual / baseline > 2) direction = 'tooMany';
            if (actual / baseline <= 0.6) direction = 'tooFew';
            if (!direction) return;

            const soapbox = blurbMap?.[direction]?.casual;
            if (soapbox && soapbox !== '') {
                list.push({
                    category,
                    blurb: soapbox,
                    header: blurbMap.headerLabel,
                });
            }
        });
        return list;
    };

    const getCategories = () => {
        const list = {};

        Object.keys(deck?.details?.powerLevel?.baseValues?.[type] || {})
            ?.filter((category) => {
                switch (category) {
                    case 'fastmana':
                    case 'taxes':
                    case 'stax':
                    case 'tutors':
                    case 'draw':
                    case 'answers':
                    case 'ramp':
                    case 'wincon':
                    case 'combo':
                        return false;
                    default:
                        return true;
                }
            })
            ?.forEach((category) => {
                let item = deck?.details?.powerLevel?.scoring?.[category];
                if (category === 'landbase' || category === 'synergy') return;
                if (!item) return;

                let subCategoryList = {};
                let items = [];
                const label = getPrettyLabel(category);

                if (item?.subCategories?.length > 0) {
                    item.subCategories.forEach((subCategory) => {
                        if (subCategory === 'landbase') return;
                        const subLabel = getPrettyLabel(subCategory);
                        const subList = deck?.details?.powerLevel?.scoring?.[subCategory]?.list || {};

                        if (subCategory === 'combos') {
                            subCategoryList[subCategory] = {
                                label: subLabel,
                                id: subCategory,
                                count: Object.keys(subList).length,
                                list: Object.keys(subList).map((_key) => {
                                    const _entry = subList[_key];
                                    return {
                                        ..._entry,
                                        type: 'combo',
                                        cards: (_entry?.cards || []).map((__card) => cards?.[__card]),
                                    };
                                }),
                                parentCategory: category,
                            };
                            items = items.concat(
                                Object.keys(subList).map((_key) => {
                                    const _entry = subList[_key];
                                    return {
                                        ..._entry,
                                        type: 'combo',
                                        cards: (_entry?.cards || []).map((__card) => cards?.[__card]),
                                    };
                                })
                            );
                        } else {
                            subCategoryList[subCategory] = {
                                label: subLabel,
                                id: subCategory,
                                count: Object.keys(subList).length,
                                list: Object.keys(subList).map((_key) => {
                                    const _card = subList[_key];
                                    return { ..._card, ...cards?.[_card?.id] };
                                }),
                                parentCategory: category,
                            };
                            items = items.concat(
                                Object.keys(subList).map((_key) => {
                                    const _card = subList[_key];
                                    return {
                                        ..._card,
                                        ...cards?.[_card?.id],
                                        id: _card?.id,
                                    };
                                })
                            );
                        }
                    });

                    const itemsHash = {};
                    items.forEach((it) => {
                        if (!itemsHash[it.id]) {
                            itemsHash[it.id] = it;
                        } else {
                            itemsHash[it.id].score = (
                                parseFloat(itemsHash[it.id].score) + parseFloat(it.score)
                            ).toFixed(1);
                        }
                    });
                    items = Object.keys(itemsHash).map((k) => itemsHash[k]);
                } else {
                    items = Object.keys(scoring?.[category]?.list || {}).map((key) => {
                        const it = scoring[category].list[key];
                        return { ...it, ...cards?.[it?.id] };
                    });
                }

                if (items?.length > 1) {
                    items = items.sort((a, b) => ((b?.score || 0) > (a?.score || 0) ? 1 : -1));
                }

                list[category] = {
                    label,
                    id: category,
                    count: items?.length || 0,
                    list: items,
                    color: '#FFFFFF',
                    subCategories: subCategoryList,
                };
            });

        Object.keys(list).forEach((key) => {
            if ((list[key]?.list?.length || 0) < 1) {
                delete list[key];
            } else {
                Object.keys(list[key]?.subCategories || {}).forEach((subKey) => {
                    if ((list[key].subCategories[subKey]?.list?.length || 0) < 1) {
                        delete list[key].subCategories[subKey];
                    }
                });
            }
        });

        return list;
    };

    const discussion = getDiscussionPoints();

    const pillarsToRender = PILLARS.filter((p) => (baseValues[p.key] || 0) > 0 || (scoring[p.key]?.score || 0) > 0);

    return (
        <BaseDeckPane
            title={'Power Levels'}
            fieldName="name"
            content={<div />}
            showScore={true}
            categories={getCategories()}
        >
            <Flex direction="column" gap="size-200">
                <section className={`powerHero powerHero--${tier.tone}`} data-tone={tier.tone}>
                    <div className="powerHeroTop">
                        <div className="powerHeroMain">
                            <span className="powerHeroEyebrow">Power Level Assessment</span>
                            <span className="powerHeroName">{tier.label}</span>
                            <span className="powerHeroHint">How hard are we trying today?</span>
                            <div className="powerHeroChips">
                                <span className="powerHeroChip">
                                    <span className="powerHeroChipLabel">Inferred type</span>
                                    <span className="powerHeroChipValue">
                                        {ratings.inferredType ? ratings.inferredType.toUpperCase() : '—'}
                                    </span>
                                </span>
                                <span className="powerHeroChip">
                                    <span className="powerHeroChipLabel">Commander tier</span>
                                    <span className="powerHeroChipValue">
                                        {ratings.commanderTier != null ? `T${ratings.commanderTier}` : '—'}
                                    </span>
                                </span>
                                {powerLevel >= 9.0 && powerLevel < 9.3 && (
                                    <span className="powerHeroChip powerHeroChip--flag">
                                        <span className="powerHeroChipLabel">Flag</span>
                                        <span className="powerHeroChipValue">Fringe cEDH</span>
                                    </span>
                                )}
                            </div>
                        </div>
                        <div className="powerHeroSide">
                            <div className="powerHeroTotalBox">
                                <span className="powerHeroTotalLabel">Power level</span>
                                <span className="powerHeroTotalValue">{powerLevelDisplay}</span>
                                <span className="powerHeroTotalUnit">/ 10</span>
                            </div>
                        </div>
                    </div>
                    <div className="powerHeroGauge">
                        <div className="powerHeroGaugeTrack">
                            <div className="powerHeroGaugeFill" style={{ width: `${pct}%` }} />
                            {[3, 5, 7, 9].map((t) => (
                                <span key={t} className="powerHeroGaugeTick" style={{ left: `${(t / 10) * 100}%` }} />
                            ))}
                            <div className="powerHeroGaugePointer" style={{ left: `${pct}%` }} aria-hidden="true" />
                        </div>
                        <div className="powerHeroGaugeScale">
                            {[0, 3, 5, 7, 9, 10].map((n) => (
                                <span key={n} style={{ left: `${(n / 10) * 100}%` }}>
                                    {n}
                                </span>
                            ))}
                        </div>
                    </div>
                </section>

                <div className="powerBaselineBar">
                    <span className="powerBaselineBarLabel">Compare pillar scores against</span>
                    <div className="powerBaselineSegmented" role="radiogroup" aria-label="Baseline">
                        <button
                            type="button"
                            className={`powerBaselineBtn${!useCEDHPowerLevels ? ' is-active' : ''}`}
                            onClick={() => setUseCEDHPowerLevels(false)}
                            role="radio"
                            aria-checked={!useCEDHPowerLevels}
                        >
                            Casual baseline
                        </button>
                        <button
                            type="button"
                            className={`powerBaselineBtn${useCEDHPowerLevels ? ' is-active' : ''}`}
                            onClick={() => setUseCEDHPowerLevels(true)}
                            role="radio"
                            aria-checked={useCEDHPowerLevels}
                        >
                            cEDH baseline
                        </button>
                    </div>
                </div>

                {pillarsToRender.length > 0 && (
                    <div className="powerPillarChart">
                        {pillarsToRender.map((p) => (
                            <PillarBar
                                key={p.key}
                                pillar={p}
                                score={scoring[p.key]?.score || 0}
                                baseline={baseValues[p.key] || 0}
                            />
                        ))}
                    </div>
                )}

                {hasFingerprintContent && (
                    <section className={`powerSection powerSection--collapsible${fingerprintOpen ? ' is-open' : ''}`}>
                        <button
                            type="button"
                            className="powerSectionToggle"
                            aria-expanded={fingerprintOpen}
                            aria-controls="powerFingerprintBody"
                            onClick={() => setFingerprintOpen((v) => !v)}
                        >
                            <span className="powerSectionEyebrow">
                                <span className="powerSectionChevron" aria-hidden="true">
                                    {fingerprintOpen ? '▾' : '▸'}
                                </span>
                                Deck fingerprint
                            </span>
                            <span className="powerSectionHint">
                                Mechanical shape of the deck and how the score drivers nudged the final power
                                number. Think of it as what the deck does, not where it sits on the ladder.
                            </span>
                        </button>

                        {fingerprintOpen && (
                            <div id="powerFingerprintBody" className="powerSectionBody">
                        {profileMarkers.length > 0 && (
                            <div className="powerPatternChipRow">
                                {profileMarkers.map((m) => (
                                    <span
                                        key={m.key}
                                        className={`powerPatternChip powerPatternChip--${m.tone}`}
                                    >
                                        {m.label}
                                    </span>
                                ))}
                            </div>
                        )}

                        <PowerLevelFingerprint profile={profile} />

                        {scoreDrivers && scoreDrivers.hasAnyContent && (
                            <div className="powerDrivers">
                                <div className="powerDriversHeader">
                                    <span className="powerDriversTitle">Score drivers</span>
                                    <span
                                        className={`powerDriversNet powerDriversNet--${scoreDrivers.netDirection}`}
                                    >
                                        {scoreDrivers.netDirection === 'up' && '▲'}
                                        {scoreDrivers.netDirection === 'down' && '▼'}
                                        {scoreDrivers.netDirection === 'flat' && '═'}{' '}
                                        {scoreDrivers.netCopy}
                                    </span>
                                </div>

                                {scoreDrivers.antiPattern && (
                                    <div className="powerAntiPattern">
                                        <span className="powerAntiPatternIcon" aria-hidden="true">
                                            ⚠
                                        </span>
                                        <span className="powerAntiPatternText">
                                            Flagged for {scoreDrivers.antiPattern.patternCount} mechanical
                                            anti-pattern
                                            {scoreDrivers.antiPattern.patternCount === 1 ? '' : 's'} (
                                            {scoreDrivers.antiPatternSeverityLabel.toLowerCase()}). The algorithm
                                            capped the final score to reflect the incoherence.
                                        </span>
                                    </div>
                                )}

                                <div className="powerDriversGrid">
                                    <div className="powerDriversColumn powerDriversColumn--boosts">
                                        <span className="powerDriversColumnHeader">
                                            ▲ Pushed the score up
                                        </span>
                                        {scoreDrivers.boosts.length === 0 ? (
                                            <span className="powerDriversEmpty">
                                                No boosts fired for this deck.
                                            </span>
                                        ) : (
                                            scoreDrivers.boosts.map((entry) => (
                                                <ScoreDriverRow
                                                    key={entry.key}
                                                    entry={entry}
                                                    direction="boost"
                                                />
                                            ))
                                        )}
                                    </div>
                                    <div className="powerDriversColumn powerDriversColumn--penalties">
                                        <span className="powerDriversColumnHeader">
                                            ▼ Pulled the score down
                                        </span>
                                        {scoreDrivers.penalties.length === 0 ? (
                                            <span className="powerDriversEmpty">
                                                No penalties fired for this deck.
                                            </span>
                                        ) : (
                                            scoreDrivers.penalties.map((entry) => (
                                                <ScoreDriverRow
                                                    key={entry.key}
                                                    entry={entry}
                                                    direction="penalty"
                                                />
                                            ))
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                            </div>
                        )}
                    </section>
                )}

                {discussion.length > 0 && type === 'casual' && (
                    <section className="powerDiscussion">
                        <header className="powerDiscussionHeader">
                            <span className="powerDiscussionEyebrow">Rule-zero talking points</span>
                            <span className="powerDiscussionHint">
                                Categories in this deck that sit far above or below typical casual-pod expectations.
                                Worth surfacing before game one.
                            </span>
                        </header>
                        <div className="powerDiscussionList">
                            {discussion.map((item) => (
                                <div className="powerDiscussionItem" key={`${type}_${item.category}`}>
                                    <span className="powerDiscussionItemHeader">{item.header}</span>
                                    <p className="powerDiscussionCopy">{item.blurb}</p>
                                </div>
                            ))}
                        </div>
                    </section>
                )}
            </Flex>
        </BaseDeckPane>
    );
}
